import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import {
  Box,
  Button,
  CircularProgress,
  GlobalStyles,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getWebSocketUrl } from "src/config";
import { useWebControlRoomSocket } from "src/hooks/useWebControlRoomSocket";
import { useResolvedRuntimeLayout } from "src/hooks/useResolvedRuntimeLayout";
import { useWebLayoutData } from "src/hooks/useWebLayoutData";
import {
  LayoutDataProvider,
  RuntimeLayoutFullscreenShell,
  useControlRoomTarget,
} from "src/shared/ui/src/display-editor";
import type { RuntimeControlRoomDispatch } from "src/shared/util/src/layout-compiler/types";
import {
  DISPLAY_TARGET_KEYS,
  type DisplayTargetKey,
} from "src/shared/util/src/layout";
import dico from "src/shared/util/src/language/dico";
import { useUserStore } from "src/stores/userStore";
import ResponsiveCompiledLayoutRenderer from "src/shared/ui/src/layout-compiler/ResponsiveCompiledLayoutRenderer";

const runtimeGlobalStyles = (
  <GlobalStyles
    styles={{
      ":root": {
        backgroundColor: "rgba(255, 255, 255, 0)",
      },
      "*": {
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      },
      "html, body": {
        margin: 0,
        padding: 0,
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "transparent",
      },
      "#root": {
        height: "100vh",
        overflow: "hidden",
        margin: 0,
        padding: 0,
      },
    }}
  />
);

/**
 * Renders a runtime display in a standalone fullscreen page without chrome.
 */
export default function LayoutDisplayPage() {
  const { id, displayTargetKey, customDisplayId } = useParams<{
    id: string;
    displayTargetKey: string;
    customDisplayId: string;
  }>();
  const { token } = useUserStore();
  const validTargetKey = customDisplayId
    ? `custom:${customDisplayId}`
    : displayTargetKey &&
        DISPLAY_TARGET_KEYS.includes(displayTargetKey as DisplayTargetKey)
      ? (displayTargetKey as DisplayTargetKey)
      : null;
  const { meet, layoutData, currentLayoutDisciplineKey } = useWebLayoutData({
    meetId: id ?? "",
    serverUrl: getWebSocketUrl(),
    token: token ?? undefined,
    enabled: Boolean(id),
  });
  const {
    customLayout,
    isLoading,
    loadLayoutFontFaces,
    targetLabel: resolvedTargetLabel,
    error,
    retry,
  } = useResolvedRuntimeLayout({
    meetId: id,
    token,
    targetKey: validTargetKey,
    disciplineKey: currentLayoutDisciplineKey,
    layoutType: "display",
    websocketUrl: getWebSocketUrl(),
  });
  const isLayoutRuntimeReady = Boolean(customLayout && meet?.id);
  const canPublishControlRoomTarget = Boolean(
    customLayout && meet?.id && id && validTargetKey,
  );
  const controlRoomSocket = useWebControlRoomSocket({
    websocketUrl: getWebSocketUrl(),
    enabled: canPublishControlRoomTarget,
  });
  const targetLabel = validTargetKey
    ? customDisplayId
      ? (resolvedTargetLabel ?? customDisplayId)
      : ((dico().licensedPortal.layouts.targetKeys as Record<string, string>)[
          validTargetKey
        ] ?? validTargetKey)
    : displayTargetKey;
  const controlRoomDispatchIdRef = useRef(0);
  const [controlRoomDispatches, setControlRoomDispatches] = useState<
    RuntimeControlRoomDispatch[]
  >([]);
  const handleControlRoomSignal = useCallback((combos: string[]) => {
    setControlRoomDispatches(
      combos.map((combo) => ({
        id: ++controlRoomDispatchIdRef.current,
        combo,
      })),
    );
  }, []);

  useControlRoomTarget({
    socket: controlRoomSocket as never,
    document: customLayout,
    meetId: id,
    targetLabel: targetLabel ?? "",
    enabled: canPublishControlRoomTarget,
    onSignal: handleControlRoomSignal,
  });

  const canRenderLayout = Boolean(
    validTargetKey && !isLoading && customLayout && isLayoutRuntimeReady,
  );

  return (
    <>
      {runtimeGlobalStyles}
      <RuntimeLayoutFullscreenShell>
        {canRenderLayout && customLayout ? (
          <LayoutDataProvider
            data={layoutData}
            getLayoutFontFaces={loadLayoutFontFaces}
          >
            <ResponsiveCompiledLayoutRenderer
              document={customLayout}
              data={layoutData}
              controlRoomDispatches={controlRoomDispatches}
            />
          </LayoutDataProvider>
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              p: 4,
              bgcolor: "white",
            }}
          >
            {error || !validTargetKey || (!isLoading && !customLayout) ? (
              <>
                <Alert severity="warning">
                  {error ||
                    (!validTargetKey
                      ? "Affichage inconnu."
                      : "Aucune maquette n’est configurée pour cet affichage et cette discipline.")}
                </Alert>
                <Button onClick={retry} variant="outlined">
                  Réessayer
                </Button>
              </>
            ) : (
              <>
                <CircularProgress />
                <Typography>
                  {meet
                    ? "Chargement de la maquette…"
                    : "En attente de la compétition en direct…"}
                </Typography>
              </>
            )}
          </Box>
        )}
      </RuntimeLayoutFullscreenShell>
    </>
  );
}
